import React, { useState } from "react";
import { Download, CheckCircle2, XCircle, Loader2, AlertCircle } from "lucide-react";
import { userService } from "../../../services/UserService";
import { useToastContext } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";

const DataExport: React.FC = () => {
  const { showToast } = useToastContext();
  const { userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const handleRequestExport = async () => {
    if (!userProfile?.emailVerified) {
      setError("Please verify your email address before requesting a data export.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);
    setUserEmail(null);

    try {
      const result = await userService.requestDataExport();
      
      // Check for success - email presence indicates 202 Accepted (fire and forget)
      if (result.data?.email) {
        setUserEmail(result.data.email);
        setSuccess(true);
        showToast(
          "success",
          `Data export request received! You'll receive an email at ${result.data.email} when your export is ready (usually within a few minutes).`
        );
      } else {
        throw new Error(result.message || "Failed to request data export");
      }
    } catch (err: any) {
      const status = err?.response?.status;
      let errorMessage = "Failed to request data export. Please try again.";

      if (status === 429) {
        errorMessage = "Too many requests. Please try again in an hour.";
      } else if (status === 400) {
        errorMessage = "Please verify your email address before requesting a data export.";
      } else if (status === 401) {
        errorMessage = "You must be logged in to request a data export.";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      showToast("error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-1.5 rounded bg-background-panel dark:bg-background-panelDark">
            <Download className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
              Data Export
            </h3>
            <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
              Request a copy of your personal data
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          {success && userEmail && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-dark">
              <CheckCircle2 className="w-3 h-3" />
              <span className="text-xs font-medium">Sent to {userEmail}</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/10 dark:bg-red-500/20 text-red-500">
              <XCircle className="w-3 h-3" />
              <span className="text-xs">{error}</span>
            </div>
          )}

          {!userProfile?.emailVerified && !success && !error && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-secondary/10 dark:bg-secondary/20 text-secondary">
              <AlertCircle className="w-3 h-3" />
              <span className="text-xs">Email not verified</span>
            </div>
          )}

          <button
            onClick={handleRequestExport}
            disabled={isLoading || !userProfile?.emailVerified || success}
            className="px-3 py-1.5 text-xs font-medium bg-background-panel dark:bg-background-panelDark hover:bg-background-default dark:hover:bg-white/5 border border-divider dark:border-divider-dark rounded text-text-primary dark:text-text-primaryDark transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Requesting...</span>
              </>
            ) : success ? (
              <>
                <CheckCircle2 className="w-3 h-3" />
                <span>Requested</span>
              </>
            ) : (
              <>
                <Download className="w-3 h-3" />
                <span>Request Export</span>
              </>
            )}
          </button>
        </div>
      </div>

      {success && userEmail && (
        <div className="mt-3 pt-3 border-t border-divider dark:border-divider-dark">
          <p className="text-xs text-text-secondary dark:text-text-secondaryDark">
            You'll receive an email at <strong className="font-semibold text-text-primary dark:text-text-primaryDark">{userEmail}</strong> when your export is ready (usually within a few minutes).
          </p>
        </div>
      )}
    </div>
  );
};

export default DataExport;
